import { useState } from 'react';
import { SearchPicker } from '@/shared/ui/components/search-picker';
import { usePickableProviders, type PickableProvider } from '@/consent/services';

/**
 * ProviderSignaturePicker — pick which provider's signature to apply to a
 * document-mode consent form (PROD-1051).
 *
 * Shown in the fill-and-sign popup ONLY when the template has a
 * provider-signature field AND the signed-in user is not a doctor (a doctor
 * auto-signs with their own signature, no picker). The filler searches the
 * org's doctors and selects the treating provider; the popup then fetches that
 * provider's stored `doctorSignatureUrl`.
 *
 * Pure selection UI — emits the chosen `PickableProvider` (or null). The
 * signature fetch + Save-gating live in the popup.
 */
export interface ProviderSignaturePickerProps {
  /** Selected provider's doctorSetupId, or null. */
  value: string | null;
  onSelect: (provider: PickableProvider | null) => void;
  disabled?: boolean;
}

export function ProviderSignaturePicker({
  value,
  onSelect,
  disabled,
}: ProviderSignaturePickerProps) {
  const { providers, isLoading } = usePickableProviders();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = providers.find((p) => p.doctorSetupId === value) ?? null;
  const options = providers.map((p) => ({
    id: p.doctorSetupId,
    label: p.name,
    secondary: p.speciality ?? undefined,
  }));

  return (
    <SearchPicker
      value={selected?.name ?? ''}
      placeholder={disabled ? '—' : 'Search provider…'}
      open={disabled ? false : open}
      search={search}
      onOpenChange={disabled ? () => {} : setOpen}
      onSearchChange={setSearch}
      loading={isLoading}
      options={options}
      onSelect={(id) =>
        onSelect(providers.find((p) => p.doctorSetupId === id) ?? null)
      }
      clearable={!!selected}
      onClear={() => onSelect(null)}
    />
  );
}
